import { createContext, Suspense, useContext, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { matchPath, Outlet, useLocation } from 'react-router';
import { CircularLoading } from '@zawarski/eti-ui';

interface ListKeepAliveState {
  /** False while the list is hidden underneath one of its child pages. */
  isShowing: boolean;
}

const ListKeepAliveContext = createContext<ListKeepAliveState>({ isShowing: true });
export const useListKeepAlive = () => useContext(ListKeepAliveContext);

interface ListKeepAliveProps {
  /** Absolute route(s) at which the list itself is the page (exact match). */
  listPath: string | string[];
  children: ReactElement;
  fallback?: ReactNode;
}

/**
 * Layout route that keeps a list page mounted underneath its child pages
 * (view / edit / new). While a child route shows, the list stays in the DOM
 * (hidden, container-relative) so returning shows it exactly as it was left -
 * scroll position, loaded row windows, search, filters - with no refetch;
 * realtime keeps patching it in the background, including the echo of the
 * user's own saves. The child renders as an opaque overlay on top.
 */
const ListKeepAlive = ({ listPath, children, fallback }: ListKeepAliveProps) => {
  const { pathname } = useLocation();
  const paths = Array.isArray(listPath) ? listPath : [listPath];
  const isShowing = paths.some((p) => matchPath({ path: p, end: true }, pathname) !== null);

  // Mount the list the first time it is visited, not when a child page is opened directly.
  const [hasShown, setHasShown] = useState(isShowing);
  if (isShowing && !hasShown) setHasShown(true);

  const state = useMemo(() => ({ isShowing }), [isShowing]);

  return (
    <div className='rep:relative rep:h-full rep:w-full'>
      {hasShown && (
        <div
          className={
            isShowing ? 'rep:contents' : 'rep:contents list-keep-alive--hidden palmetto-print-hide'
          }
          style={isShowing ? undefined : { visibility: 'hidden' }}>
          <ListKeepAliveContext.Provider value={state}>{children}</ListKeepAliveContext.Provider>
        </div>
      )}
      {!isShowing && (
        <div className='rep:absolute rep:inset-0 rep:z-30 rep:overflow-hidden rep:bg-background'>
          <Suspense
            fallback={
              fallback ?? (
                <div className='rep:flex rep:h-full rep:w-full rep:items-center rep:justify-center'>
                  <CircularLoading size='md' />
                </div>
              )
            }>
            <Outlet />
          </Suspense>
        </div>
      )}
    </div>
  );
};

export default ListKeepAlive;
